import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
  }, [params]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      referral_code: form.referral_code.trim() || undefined,
    };
    const res = await register(payload);
    setBusy(false);
    if (res.ok) {
      toast.success("Account created — welcome");
      nav("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#07080a] noise flex">
      <div className="hidden md:block flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1680448588521-35f5d04a4193?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNyeXB0byUyMGJsb2NrY2hhaW4lMjBnb2xkfGVufDB8fHx8MTc4NDEwNzU2NHww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-[#07080a]" />
        <div className="absolute bottom-10 left-10 max-w-md">
          <div className="font-display text-4xl leading-tight">Membership begins with a <span className="text-gold italic">signal</span>.</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-3 mb-10">
            <div className="w-8 h-8 rounded-sm bg-gold flex items-center justify-center">
              <span className="font-display text-black text-lg font-bold">V</span>
            </div>
            <span className="font-display text-lg">Vision <span className="text-gold">Fashions</span></span>
          </Link>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-3">Create account</h1>
          <p className="text-slate-400 mb-8">Use a referral code to unlock the 3× tier.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label className="text-slate-300">Full name</Label>
              <Input
                data-testid="reg-name"
                required
                value={form.name}
                onChange={onChange("name")}
                className="mt-2 bg-[#111317] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                data-testid="reg-email"
                type="email"
                required
                value={form.email}
                onChange={onChange("email")}
                className="mt-2 bg-[#111317] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                data-testid="reg-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={onChange("password")}
                className="mt-2 bg-[#111317] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <Label className="text-slate-300">Referral code <span className="text-slate-500">(optional)</span></Label>
              <Input
                data-testid="reg-referral"
                value={form.referral_code}
                onChange={onChange("referral_code")}
                className="mt-2 bg-[#111317] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11 font-mono-x"
                placeholder="ABC123XY"
              />
            </div>
            {error && (
              <div data-testid="reg-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3 rounded-md">
                {error}
              </div>
            )}
            <Button
              data-testid="reg-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-gold text-black hover:bg-[#f3e396] h-11 rounded-full"
            >
              {busy ? "Creating…" : "Create account"}
            </Button>
            <div className="text-sm text-slate-400 text-center">
              Already registered?{" "}
              <Link to="/login" data-testid="reg-to-login" className="text-gold hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
